import {
  FileDropzone,
  FileProgress,
  FileUploadModal,
} from '@app/components/FileUpload'
import InfiniteScrollTable from '@app/components/InfiniteScrollTable'
import SearchBar from '@app/components/SearchBar'
import { shareAttachmentActionCreators } from '@app/components/ShareAttachmentModal/modules/shareAttachment'
import { IForm } from '@app/models/Form'
import { bindFunctions } from '@app/utils/class'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Column } from 'react-virtualized'
import { Nav, NavItem, NavLink } from 'reactstrap'
import {
  actionCreators,
  FormsScope,
  IFormsListState,
} from '../modules/formsList'
import { IAttachment } from '@app/models/Attachment'

type ComponentProps = IFormsListState &
  typeof actionCreators &
  typeof shareAttachmentActionCreators

interface IComponentState {
  isOpen: any
}

export default class AssignmentsListPage extends React.PureComponent<
  ComponentProps,
  IComponentState
> {
  private tableRef: React.RefObject<InfiniteScrollTable>

  constructor(props: ComponentProps) {
    super(props)

    bindFunctions(this)

    this.tableRef = React.createRef<InfiniteScrollTable>()

    this.state = {
      isOpen: {},
    }
  }

  public render() {
    return (
      <DocumentTitle title='Forms'>
        <div className='container d-flex flex-column h-100 pt-3'>
          <div>
            <h3>Forms</h3>
            <SearchBar
              onChange={this._onChangeFilter}
              placeholder='Looking for something?'
              size='sm'
              value={this.props.filterText}
            />
            {this._renderFileUpload()}
            <div className='mb-3 mt-5'>
              <Nav tabs>
                <NavItem>
                  <NavLink
                    active={this.props.scope === FormsScope.Mine}
                    onClick={() => this._onChangeScope(FormsScope.Mine)}
                  >
                    {FormsScope.Mine}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    active={this.props.scope === FormsScope.All}
                    onClick={() => this._onChangeScope(FormsScope.All)}
                  >
                    {FormsScope.All}
                  </NavLink>
                </NavItem>
              </Nav>
            </div>
          </div>
          <div>
            <InfiniteScrollTable
              isLoading={this.props.isLoading}
              items={this.props.items}
              rowHeight={50}
              loadItems={this.props.loadForms}
              noRowsRenderer={this._noRowsRenderer}
              totalItems={this.props.totalItems}
              ref={this.tableRef}
            >
              <Column
                cellDataGetter={({ rowData }) => {
                  const form = rowData as IForm

                  return form && form.file
                    ? form.file.title
                    : 'No file attached (SHOULD BE DELETED)'
                }}
                dataKey='name'
                flexGrow={1}
                label='Name'
                width={100}
              />
              <Column
                cellRenderer={({ rowData }) => {
                  const form = rowData as IForm

                  if (!form.file) {
                    return null
                  }

                  return (
                    <a
                      href={`/api/Files/${form.file.id}/Download`}
                      target='_blank'
                      rel='noreferrer'
                      className='text-primary'
                    >
                      <FontAwesomeIcon icon={['fal', 'download']} fixedWidth />
                    </a>
                  )
                }}
                dataKey='download'
                width={30}
              />
              <Column
                cellRenderer={({ rowData }) => {
                  const form = rowData as IForm

                  if (!form.file) {
                    return null
                  }

                  return (
                    <a
                      className='text-primary'
                      onClick={() =>
                        this.props.showShareAttachmentModal(form.file)
                      }
                    >
                      <FontAwesomeIcon icon={['fal', 'share-alt']} fixedWidth />
                    </a>
                  )
                }}
                dataKey='share'
                width={30}
              />
              <Column
                cellRenderer={({ rowData }) => {
                  const form = rowData as IForm

                  return (
                    <div className='text-primary'>
                      <FileUploadModal
                        onCancel={() =>
                          this.setState({ isOpen: { [form.fileId]: false } })
                        }
                        identifier='forms-list'
                        onSave={(e: IAttachment) => {
                          this.setState({ isOpen: { [form.fileId]: false } })
                          this.props.setAttachment(e, form.id)
                        }}
                        attachment={form.file}
                        isOpen={this.state.isOpen[form.fileId]}
                      />
                      <div
                        onClick={() =>
                          this.setState({ isOpen: { [form.fileId]: true } })
                        }
                      >
                        <FontAwesomeIcon icon={['fal', 'pencil']} fixedWidth />
                      </div>
                    </div>
                  )
                }}
                dataKey='edit'
                width={30}
              />
              <Column
                cellRenderer={({ rowData }) => {
                  const form = rowData as IForm

                  return (
                    <div
                      className='text-danger'
                      onClick={(e) => this.props.removeForm(form.id)}
                    >
                      <FontAwesomeIcon icon={['fal', 'trash']} fixedWidth />
                    </div>
                  )
                }}
                dataKey='trash'
                width={30}
              />
            </InfiniteScrollTable>
          </div>
        </div>
      </DocumentTitle>
    )
  }

  private _renderFileUpload() {
    const dropzoneStyle: React.CSSProperties = {
      borderColor: 'rgb(102, 102, 102)',
      borderRadius: '5px',
      borderStyle: 'dashed',
      borderWidth: '1px',
      display: 'table',
      height: '100px',
      width: '100%',
    }

    const dropzoneActiveStyle: React.CSSProperties = {
      borderColor: '#9aa41a',
      borderWidth: '2px',
    }

    return (
      <div className='mt-4'>
        <FileDropzone
          style={dropzoneStyle}
          identifier='forms-file-upload'
          activeStyle={dropzoneActiveStyle}
        >
          <p
            style={{
              display: 'table-cell',
              textAlign: 'center',
              verticalAlign: 'middle',
            }}
          >
            Drag and drop or click here to select files
          </p>
        </FileDropzone>
        <FileProgress identifier='forms-file-upload' />
      </div>
    )
  }

  private _noRowsRenderer() {
    return <div>No forms</div>
  }

  private _onChangeFilter(newFilter: string) {
    this.props.setFilterText(newFilter)
    this._reloadData()
  }

  private _onChangeScope(newScope: FormsScope) {
    this.props.setScope(newScope)
    this._reloadData()
  }

  private _reloadData() {
    if (this.tableRef.current) {
      this.tableRef.current.forceReloadData()
    }
  }
}
